import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AuthService } from '../../service/auth/auth.service';
import { PluginService } from '../../service/plugin.service';
import { ReqResService } from '../../service/req-res.service';

declare var toastr: any;
declare var $: any;

@Component({
  selector: 'app-basket',
  templateUrl: './basket.component.html',
  styleUrls: ['./basket.component.css']
})
export class BasketComponent implements OnInit {
  basketData: any[] = [];
  public _pluginservice: any;

  orderDescription: string = '';
  selectedImage: string = '';
  private readonly defaultImage = '../assets/img/products/0.png';

  approvers: any[] = [];
  filteredApprovers: any[] = [];
  approverSearchText: string = '';
  selectedApproverId: number | null = null;
  selectedApprover: any = null;
  isSubmittingOrder: boolean = false;

  constructor(
    private reqRes: ReqResService,
    private pluginservice: PluginService,
    private authService: AuthService,
    private titleService: Title,
    private router: Router
  ) {
    this.titleService.setTitle('Basket');
    this._pluginservice = this.pluginservice;
  }

  ngOnInit(): void {
    this.waitAndLoadBasket();
    this.loadApprovers();
  }

  waitAndLoadBasket(): void {
    let retryCount = 0;
    const maxRetry = 20;

    const interval = setInterval(() => {
      const userId = this.authService.getuserid();

      if (userId) {
        clearInterval(interval);
        this.loadBasket();
      }

      retryCount++;

      if (retryCount >= maxRetry) {
        clearInterval(interval);
        console.warn('User id not ready, basket not loaded.');
      }
    }, 100);
  }

  loadBasket(): void {
    this.basketData = this.getBasket().map((x: any) => ({
      ...x,
      img: x.img || this.defaultImage,
      selected: !!x.selected,
      orderQty: Number(x.orderQty || 1),
      price: Number(x.price || 0)
    }));
  }

  loadApprovers(): void {
    this.reqRes.getData('/Order/GetApproverList').subscribe(
      (res: any) => {
        this.approvers = Array.isArray(res) ? res : [];
        this.filteredApprovers = [...this.approvers];
      },
      (error: any) => {
        console.error('GetApproverList error:', error);
        toastr?.error('Approver list could not be loaded', '', { timeOut: 1500 });
      }
    );
  }

  filterApprovers(): void {
    const search = (this.approverSearchText || '').trim().toLowerCase();

    if (!search) {
      this.filteredApprovers = [...this.approvers];
      return;
    }

    this.filteredApprovers = this.approvers.filter(x =>
      (x.name || '').toLowerCase().includes(search) ||
      String(x.id || '').includes(search)
    );
  }

  selectApprover(approver: any): void {
    this.selectedApproverId = approver.id;
    this.selectedApprover = approver;
  }

  submitIfSingleResult(): void {
    if (this.filteredApprovers.length === 1) {
      this.selectApprover(this.filteredApprovers[0]);
    }
  }

  getApproverInitials(name: string): string {
    if (!name) return '?';

    const parts = name.trim().split(' ').filter((x: string) => x);
    if (parts.length === 1) {
      return parts[0].substring(0, 1).toUpperCase();
    }

    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  openApproverModal(): void {
    this.approverSearchText = '';
    this.filteredApprovers = [...this.approvers];
    this.selectedApproverId = null;
    this.selectedApprover = null;
    ($('#approverModal') as any).modal('show');

    setTimeout(() => {
      const el = document.querySelector('#approverModal input') as HTMLInputElement;
      if (el) {
        el.focus();
      }
    }, 300);
  }

  getBasket(): any[] {
    const userKey = this.getCurrentUserKey();
    const storageKey = `basket_${userKey}`;
    return JSON.parse(localStorage.getItem(storageKey) || '[]');
  }

  saveBasket(): void {
    const userKey = this.getCurrentUserKey();
    const storageKey = `basket_${userKey}`;
    localStorage.setItem(storageKey, JSON.stringify(this.basketData));
    window.dispatchEvent(new Event('basketUpdated'));
  }

  getCurrentUserKey(): string {
    const user = this.authService.getuserid().toString();
    return user;
  }

  increaseQty(item: any): void {
    item.orderQty = Number(item.orderQty || 0) + 1;
    this.saveBasket();
  }

  decreaseQty(item: any): void {
    if (Number(item.orderQty || 0) > 1) {
      item.orderQty--;
      this.saveBasket();
    }
  }

  onQtyChange(item: any): void {
    item.orderQty = Number(item.orderQty || 1);

    if (isNaN(item.orderQty) || item.orderQty < 1) {
      item.orderQty = 1;
    }

    this.saveBasket();
  }

  onItemSelectChange(): void {
    this.saveBasket();
  }

  toggleCheckAll(event: any): void {
    const checked = !!event.target.checked;

    this.basketData.forEach(item => {
      item.selected = checked;
    });

    this.saveBasket();
  }

  get isAllSelected(): boolean {
    return this.basketData.length > 0 && this.basketData.every(x => x.selected);
  }

  get selectedItems(): any[] {
    return this.basketData.filter(x => x.selected);
  }

  removeItem(itemCode: string): void {
    this.basketData = this.basketData.filter(x => x.itemCode !== itemCode);
    this.saveBasket();
    toastr?.success('Item removed', '', { timeOut: 1000 });
  }

  get totalItems(): number {
    return new Set(this.basketData.map(x => x.itemCode)).size;
  }

  get totalQty(): number {
    return this.basketData.reduce((sum, item) => {
      return sum + Number(item.orderQty || 0);
    }, 0);
  }

  get totalPrice(): number {
    return this.basketData.reduce(
      (sum, item) => sum + (Number(item.price || 0) * Number(item.orderQty || 0)),
      0
    );
  }

  get selectedTotalItems(): number {
    return new Set(this.selectedItems.map(x => x.itemCode)).size;
  }

  get selectedTotalQty(): number {
    return this.selectedItems.reduce((sum, item) => {
      return sum + Number(item.orderQty || 0);
    }, 0);
  }

  get selectedTotalPrice(): number {
    return this.selectedItems.reduce(
      (sum, item) => sum + (Number(item.price || 0) * Number(item.orderQty || 0)),
      0
    );
  }

  submitOrder(): void {
    const selected = this.selectedItems;

    if (!selected.length) {
      toastr?.warning('Please select item(s)', '', { timeOut: 1000 });
      return;
    }

    if (!this.approvers.length) {
      toastr?.warning('Approver list is empty', '', { timeOut: 1000 });
      return;
    }

    this.openApproverModal();
  }

  confirmSubmitOrder(): void {
    const selected = this.selectedItems;

    if (!selected.length) {
      toastr?.warning('Please select item(s)', '', { timeOut: 1000 });
      return;
    }

    if (!this.selectedApproverId) {
      toastr?.warning('Please select an approver', '', { timeOut: 1000 });
      return;
    }

    const payload = {
      orderId: 0,
      approverId: this.selectedApproverId,
      userId: Number(this.getCurrentUserKey()),
      orderDescription: this.orderDescription,
      type: 'Basket',
      items: selected.map(item => ({
        itemId: item.itemId,
        itemCode: item.itemCode,
        itemName: item.itemName,
        itemUnit: item.itemUnit,
        itemImg: item.img,
        orderQty: Number(item.orderQty || 0),
        groupId: item.groupId,
        groupName: item.groupName,
        price: Number(item.price || 0),
        note: item.note || ''
      }))
    };

    this.saveSelectedOrder(payload);
  }

  saveSelectedOrder(payload: any): void {
    this.isSubmittingOrder = true;

    this.reqRes.postData('/Orders/SaveBasketOrder', payload).subscribe(
      (res: any) => {
        this.isSubmittingOrder = false;
        ($('#approverModal') as any).modal('hide');

        toastr?.success('Order saved successfully', '', { timeOut: 1000 });

        const selectedCodes = this.selectedItems.map(x => x.itemCode);
        this.basketData = this.basketData.filter(x => !selectedCodes.includes(x.itemCode));

        this.orderDescription = '';
        this.selectedApproverId = null;
        this.selectedApprover = null;
        this.approverSearchText = '';

        this.saveBasket();
        this.router.navigate(['/orders']);

        console.log('API response:', res);
      },
      (error: any) => {
        this.isSubmittingOrder = false;
        console.error('SaveBasketOrder error:', error);
        toastr?.error('Order save failed', '', { timeOut: 1000 });
      }
    );
  }

  trackByItem(index: number, item: any): any {
    return item?.itemCode || index;
  }

  openImagePreview(img: string): void {
    this.selectedImage = img || this.defaultImage;
    ($('#imagePreviewModal') as any).modal('show');
  }

  onImageError(event: any): void {
    if (event?.target && event.target.src !== this.defaultImage) {
      event.target.src = this.defaultImage;
    }
  }
}
